import isEqual from 'lodash-es/isEqual';
import { singleClick } from 'ol/events/condition';
import MultiPolygon from 'ol/geom/MultiPolygon';
import MultiPoint from 'ol/geom/MultiPoint';
import { MultiLineString } from 'ol/geom';

import { getSelectStyle } from 'cadenza/map/style/select-styles';
import { TooltipInteraction } from 'cadenza/map/custom/map-client-edit-geometry/tooltip-interaction';
import { ClassicCadenzaModifyEndEvent } from 'cadenza/map/interactions/classic/classic-modify-interaction';
import { FeatureProperty } from 'cadenza/map/feature-property';
import { assert } from 'cadenza/utils/custom-error';

import type VectorSource from 'ol/source/Vector';
import type VectorLayer from 'ol/layer/Vector';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type { Coordinate } from 'ol/coordinate';
import type Feature from 'ol/Feature';
import type OlMap from 'ol/Map';

import i18n from './messages.properties';

export class ClientGeometryDeleteInteraction extends TooltipInteraction {

  #drawLayer: VectorLayer<VectorSource>;

  constructor (map: OlMap, drawLayer: VectorLayer<VectorSource>) {
    super(map, i18n('deleteTooltip'), { handleEvent: (event: MapBrowserEvent<MouseEvent>) => this._handleEvent(event), style: getSelectStyle });
    this.#drawLayer = drawLayer;
  }

  _handleEvent (event: MapBrowserEvent<MouseEvent>) {
    if (!singleClick(event)) {
      return true; // continue event propagation
    }
    const source = this.#drawLayer.getSource();
    assert(source, 'Source cannot be null');
    const clickCoordinate = this.map.getCoordinateFromPixel(event.pixel);
    const modifiedFeatures: Feature<MultiPoint | MultiLineString | MultiPolygon>[] = [];
    this.map.forEachFeatureAtPixel(
      event.pixel,
      (feature: Feature) => forFeatureAtPixel(feature, modifiedFeatures, clickCoordinate, source),
      { hitTolerance: 10 }
    );
    if (modifiedFeatures.length) {
      source.dispatchEvent(new ClassicCadenzaModifyEndEvent(modifiedFeatures));
    }
  }

}

function forFeatureAtPixel (feature: Feature, modifiedFeatures: Feature[], clickCoordinate: Coordinate, source: VectorSource) {
  const geometry = feature.getGeometry();
  if (geometry instanceof MultiPoint || geometry instanceof MultiLineString || geometry instanceof MultiPolygon) {
    feature.set(FeatureProperty.OLD_GEOMETRY, geometry.clone());
    if (geometry instanceof MultiPoint) {
      geometry.setCoordinates(getPointsNotIntersecting(geometry, clickCoordinate).map(geom => geom.getCoordinates()));
    } else if (geometry instanceof MultiLineString) {
      geometry.setCoordinates(getLinesNotIntersecting(geometry, clickCoordinate).map(geom => geom.getCoordinates()));
    } else {
      geometry.setCoordinates(getPolygonsNotIntersecting(geometry, clickCoordinate).map(geom => geom.getCoordinates()));
    }
    modifiedFeatures.push(feature);
  } else {
    source.removeFeature(feature);
  }
}

function getPointsNotIntersecting (geometry: MultiPoint, clickCoordinate: Coordinate) {
  const pointCoordsToDelete = geometry.getClosestPoint(clickCoordinate);
  return geometry.getPoints().filter(point => !isEqual(point.getCoordinates(), pointCoordsToDelete));
}

function getLinesNotIntersecting (geometry: MultiLineString, clickCoordinate: Coordinate) {
  const lineStrings = geometry.getLineStrings();
  const distanceToClickCoordinate = lineStrings.map(line => {
    return calculateDistanceBetweenPositions(line.getClosestPoint(clickCoordinate), clickCoordinate);
  });
  const minDistance = Math.min(...distanceToClickCoordinate);
  const indexOfClosestLine = distanceToClickCoordinate.indexOf(minDistance);
  const closestLine = lineStrings[indexOfClosestLine];
  return lineStrings.filter(line => line !== closestLine);
}

function getPolygonsNotIntersecting (geometry: MultiPolygon, clickCoordinate: Coordinate) {
  return geometry.getPolygons().filter(polygon => !polygon.intersectsCoordinate(clickCoordinate));
}

function calculateDistanceBetweenPositions (coord1: number[], coord2: number[]) {
  const deltaX = coord1[0] - coord2[0];
  const deltaY = coord1[1] - coord2[1];
  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
}
